<template>
  <div class="card mb-4 cursor-pointer min-w-0" :class="mindsetCardClass">
    <div class="flex flex-row flex-wrap sm:flex-nowrap items-stretch sm:items-center p-2 sm:p-3 gap-2 min-w-0" @click.prevent="toggleDetails"
      data-testid="mindset360-card">
      <div class="flex flex-col flex-1 min-w-0 order-1">
        <div class="flex justify-between items-start gap-2 min-w-0">
          <h3 class="text-base sm:text-lg truncate break-words flex-1 min-w-0">{{ displayedSchemaName }}</h3>
        </div>
        <div v-if="displayedUsersCount > 0"
          class="flex justify-start items-center flex-wrap gap-x-2 mt-1 min-w-0 text-sm sm:text-base font-normal">
          <font-awesome-icon icon="fa-users" class="shrink-0"/>
          <span class="break-words">{{ displayedUsersCount + ' ' + $t('Mindset360Display.participants', displayedUsersCount) }} |</span>
          <span class="text-[var(--color-orange)]">{{ participantsPercent }}%</span>
        </div>
        <div class="flex flex-wrap items-center gap-2 mt-1 min-w-0">
          <div class="flex flex-row py-1 px-2.5 items-center min-h-0 w-min text-white rounded shrink-0"
            :style="isActive">
            <font-awesome-icon icon="fa-calendar" class="shrink-0 w-4 sm:w-5"/>
            <span class="py-0.5 px-1 text-xs sm:text-sm">{{ formattedDate }}</span>
          </div>
        </div>
      </div>
      <div class="flex items-center order-2 sm:order-3 flex-shrink-0 self-center gap-x-2">
        <i v-if="editable !== false && status"
            class="shrink-0 text-base sm:text-xl text-[var(--color-disabled)] hover:text-[var(--color-orange)] cursor-pointer pl-1"
            @click.stop="toggleEditMode">
            <font-awesome-icon icon="fa-pen"/>
          </i>
        <font-awesome-icon
          v-if="!showContainer"
          icon="fa-solid fa-chevron-down"
          class="text-gray-400 hover:text-gray-600 transition-colors" />
        <font-awesome-icon
          v-else
          icon="fa-solid fa-chevron-up"
          class="text-gray-400 hover:text-gray-600 transition-colors" />
      </div>
    </div>
    <div v-if="showContainer" class="cursor-pointer">
      <div class="flex flex-col border-t pt-4">
      <div class="block box-border py-5 px-4 sm:px-7">
        <!-- Edit Mode: Add Participants -->
        <div v-if="isEditMode" class="mb-4">
          <MultiSelect
            v-model="selectedNewParticipants"
            display="chip"
            :options="availableParticipants"
            optionLabel="name"
            filter
            :placeholder="$t('Mindset360Display.addParticipants')"
            class="w-full" />

          <!-- External Emails Input (only if schema allows) -->
          <external-users-invite-component
            v-if="showExternalEmailsInput"
            class="relative mt-3 w-full"
          />
        </div>

        <!-- Participants List -->
        <div class="flex flex-row flex-wrap justify-start gap-1">
          <div v-for="participant in displayedUserDetails" :key="participant._id"
            class="flex items-center rounded-3xl py-1 px-3 text-sm sm:text-base m-0 text-white"
            :style="getParticipantStyle(participant)">
            {{ participant.name }}
            <!-- Delete button (only in edit mode, and only for non-submitted assessments) -->
            <span v-if="isEditMode && canDeleteParticipant(participant)"
              class="ml-2 cursor-pointer hover:text-red-300"
              @click.stop="markForRemoval(participant)">
              <font-awesome-icon icon="fa-times" class="text-sm"/>
            </span>
            <!-- Undo button for participants marked for removal -->
            <span v-if="isEditMode && isMarkedForRemoval(participant)"
              class="ml-2 cursor-pointer hover:text-green-300"
              @click.stop="unmarkForRemoval(participant)">
              <font-awesome-icon icon="fa-undo" class="text-sm"/>
            </span>
          </div>
        </div>

        <!-- Edit Mode: Save/Cancel Buttons -->
        <div v-if="isEditMode" class="relative flex flex-col-reverse sm:flex-row items-stretch sm:items-end justify-end mt-6 sm:mt-8 box-border gap-2 sm:gap-3">
          <Button class="btn-secondary w-full sm:w-60 min-h-fit py-2 px-6 text-sm sm:text-base"
            @click.stop="cancelEdit"
            unstyled>
            {{ $t('Mindset360Display.cancel') }}
          </Button>
          <Button class="btn-primary-accent w-full sm:w-60 min-h-fit px-6 py-2 text-sm sm:text-base"
            @click.stop="saveChanges"
            :disabled="!hasChanges || !isValidParticipantCount"
            unstyled>
            {{ $t('Mindset360Display.save') }}
          </Button>
        </div>

        <!-- Normal Mode: Extend/Remind Buttons -->
        <div class="relative flex flex-col-reverse sm:flex-row items-stretch sm:items-end justify-end mt-6 sm:mt-8 box-border gap-2 sm:gap-3"
          v-if="!isEditMode && participantsPercent !== 100 && this.status">
          <Button class="btn-secondary w-full sm:w-60 min-h-fit py-2 px-6 text-sm sm:text-base disabled:opacity-20 disabled:cursor-not-allowed"
            @click="extendDuration" :disabled="enableExtension"
            v-tooltip="this.extendDurationTooltip"
            unstyled>
            {{ $t('Mindset360Display.extendDuration') }}
          </Button>
          <Button class="btn-primary-accent w-full sm:w-60 min-h-fit px-6 py-2 text-sm sm:text-base disabled:opacity-20 disabled:cursor-not-allowed"
            @click="sendReminder"
            unstyled>
            {{ $t('Mindset360Display.sendReminder') }}
          </Button>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>
<script>
import ExternalUsersInviteComponent from '@/components/Home/KssCreator/ExternalUsersInviteComponent.vue';
import { Button, MultiSelect } from 'primevue';
import { mapGetters } from 'vuex';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'Mindset360CardComponent',
  props: ['mindset'],
  components: {
    ExternalUsersInviteComponent,
    Button,
    MultiSelect,
  },
  data() {
    return {
      status: (this.mindset.status === 'active'),
      showContainer: false,
      usersCount: this.mindset.involvedUsers.length || 0,
      selectedDate: new Date(this.mindset.endDate),
      editable: true,
      isEditMode: false,
      selectedNewParticipants: [],
      assessmentsToRemove: [], // Assessment IDs marked for removal
    };
  },
  computed: {
    ...mapGetters('assessmentSchemas', ['getExternalEvaluationSchemas']),
    // Get the schema name from representation properties, fallback to translation
    displayedSchemaName() {
      // eslint-disable-next-line prefer-destructuring
      const schemaName = this.mindset.schemaName;
      if (schemaName) {
        const locale = this.$i18n?.locale || 'en';
        return schemaName[locale] || schemaName.en || this.$t('Mindset360Display.heading');
      }
      return this.$t('Mindset360Display.heading');
    },
    mindsetCardClass() {
      return this.status ? '' : 'card-inactive';
    },
    participantsPercent() {
      const percent = this.mindset.percent.toFixed(2);
      return Number(percent);
    },
    formattedDate() {
      const source = this.mindset?.endDate || this.selectedDate;
      if (!source) {
        return '';
      }
      const selectedDate = source instanceof Date ? source : new Date(source);
      if (Number.isNaN(selectedDate.getTime())) {
        return '';
      }
      return `${(`0${selectedDate.getDate()}`).slice(-2)}.${(`0${selectedDate.getMonth() + 1}`).slice(-2)}.${selectedDate.getFullYear()}`;
    },
    involvedUsers() {
      return this.mindset.userDetails;
    },
    displayedUserDetails() {
      // Show all current participants (with visual indication if marked for removal)
      return this.mindset.userDetails;
    },
    displayedUsersCount() {
      // Calculate the count based on current state + changes
      const currentCount = this.mindset.involvedUsers.length || 0;
      if (!this.isEditMode) {
        return currentCount;
      }
      return currentCount - this.assessmentsToRemove.length + this.selectedNewParticipants.length;
    },
    enableExtension() {
      return !this.mindset.extendDuration;
    },
    isActive() {
      return this.status ? 'background-color: var(--color-orange);' : 'background-color: var(--color-inactive-dark);';
    },
    extendDurationTooltip() {
      if (this.enableExtension) {
        return this.$t('Mindset360Display.3DayMessage');
      }
      return '';
    },
    // Get participants available to add (not already in the series)
    availableParticipants() {
      const existingUserIds = this.mindset.involvedUsers || [];
      const allParticipants = this.$store.getters.getExternalEvaluationParticipants || [];
      return allParticipants.filter((p) => !existingUserIds.includes(p._id));
    },
    // Check if external emails should be shown based on schema config
    showExternalEmailsInput() {
      const schemaId = this.mindset.generatedFromSchema;
      if (!schemaId) return false;
      const schema = this.getExternalEvaluationSchemas.find((s) => s._id === schemaId);
      return schema?.canDisplayExternalEmails || false;
    },
    // Check if there are any pending changes
    hasChanges() {
      return this.selectedNewParticipants.length > 0
        || this.assessmentsToRemove.length > 0
        || this.externalEmails.length > 0;
    },
    // Get external emails from store
    externalEmails() {
      return this.$store.getters.getInviteEmails || [];
    },
    // Get min/max participants from schema config
    schemaParticipantLimits() {
      const schemaId = this.mindset.generatedFromSchema;
      if (!schemaId) return { min: 7, max: 10 };
      const schema = this.getExternalEvaluationSchemas.find((s) => s._id === schemaId);
      return {
        min: schema?.minParticipants || 7,
        max: schema?.maxParticipants || 10,
      };
    },
    // Calculate if the final participant count is valid based on schema config
    isValidParticipantCount() {
      const currentCount = this.mindset.involvedUsers.length || 0;
      const finalCount = currentCount
        - this.assessmentsToRemove.length
        + this.selectedNewParticipants.length
        + (this.showExternalEmailsInput ? this.externalEmails.length : 0);
      const { min, max } = this.schemaParticipantLimits;
      return finalCount >= min && finalCount <= max;
    },
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'externalEvaluation',
        this.$t('FallbackTexts.externalEvaluation'),
        this.$i18n?.locale,
      );
    },
  },
  watch: {
    'mindset.endDate'(newEnd) {
      if (newEnd) {
        this.selectedDate = new Date(newEnd);
      }
    },
    'mindset.status'(newStatus) {
      this.status = newStatus === 'active';
    },
  },
  methods: {
    toggleDetails() {
      if (!this.isEditMode) {
        this.showContainer = !this.showContainer;
      }
    },
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (this.isEditMode) {
        this.showContainer = true;
        // Reset participants list before loading to prevent duplicates
        this.$store.commit('resetExternalEvaluationParticipants');
        // Load participants list for the MultiSelect
        this.$store.dispatch('getExternalEvaluationParticipants', this.$store.getters.getUser?.name);
        // Fetch assessment schemas to check canDisplayExternalEmails
        this.$store.dispatch('assessmentSchemas/fetchAssessments');
        // Reset external emails
        this.$store.commit('resetInviteEmail');
      } else {
        this.cancelEdit();
      }
    },
    cancelEdit() {
      this.isEditMode = false;
      this.selectedNewParticipants = [];
      this.assessmentsToRemove = [];
      this.$store.commit('resetInviteEmail');
    },
    async saveChanges() {
      if (!this.hasChanges) {
        this.cancelEdit();
        return;
      }

      // Validate participant count
      if (!this.isValidParticipantCount) {
        const { min, max } = this.schemaParticipantLimits;
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.mindset360.mindset360UserCount', { min, max }),
          detail: '',
          life: 3000,
        });
        return;
      }

      // Check for duplicate external emails (emails that already exist in participants)
      if (this.showExternalEmailsInput && this.externalEmails.length > 0) {
        const existingEmails = this.mindset.userDetails
          .filter((u) => u.email)
          .map((u) => u.email.toLowerCase());
        const duplicateEmails = this.externalEmails
          .filter((email) => existingEmails.includes(email.toLowerCase()));
        if (duplicateEmails.length > 0) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('ToastComponent.mindset360.duplicateEmail'),
            detail: duplicateEmails.join(', '),
            life: 5000,
          });
          return;
        }
      }

      try {
        const data = {
          addUsers: this.selectedNewParticipants.map((p) => p._id),
          addExternalUsers: this.showExternalEmailsInput ? this.externalEmails : [],
          removeAssessmentIds: this.assessmentsToRemove,
        };

        await this.$store.dispatch('updateExternalEvaluation', {
          seriesId: this.mindset.seriesId,
          data,
        });

        this.$toast.add({
          severity: 'success',
          summary: this.$t('ToastComponent.mindset360.mindset360Updated', { text: this.featureName }),
          detail: '',
          life: 3000,
        });

        // Refresh the data
        await this.$store.dispatch('getExternalEvaluation');
        this.cancelEdit();
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: error.response?.data?.message || this.$t('ToastComponent.mindset360.updateError', { text: this.featureName }),
          detail: '',
          life: 3000,
        });
      }
    },
    canDeleteParticipant(participant) {
      // Can only delete if not submitted and not already marked for removal
      return participant.status !== 'submitted' && !this.isMarkedForRemoval(participant);
    },
    isMarkedForRemoval(participant) {
      return this.assessmentsToRemove.includes(String(participant._id));
    },
    markForRemoval(participant) {
      if (!this.isMarkedForRemoval(participant)) {
        this.assessmentsToRemove.push(String(participant._id));
      }
    },
    unmarkForRemoval(participant) {
      const index = this.assessmentsToRemove.indexOf(String(participant._id));
      if (index > -1) {
        this.assessmentsToRemove.splice(index, 1);
      }
    },
    getParticipantStyle(participant) {
      if (this.isMarkedForRemoval(participant)) {
        return 'background-color: var(--color-inactive-dark); text-decoration: line-through;';
      }
      if (participant.status === 'submitted') {
        return 'background-color: var(--color-light-grey);';
      }
      return 'background-color: var(--color-orange);';
    },
    async sendReminder() {
      // Uses seriesId parameter
      await this.$store.dispatch('sendExternalEvaluationReminder', this.mindset.seriesId);
      this.$toast.add({
        severity: 'success',
        summary: this.$t('Mindset360Display.reminderNotification'),
        detail: '',
        life: 3000,
      });
      await this.$store.dispatch('getExternalEvaluation');
    },
    async extendDuration() {
      // Uses seriesId parameter — store refreshes list; card watches endDate for display
      await this.$store.dispatch('extendExternalEvaluation', this.mindset.seriesId);
      if (this.mindset?.endDate) {
        this.selectedDate = new Date(this.mindset.endDate);
      }
      this.$toast.add({
        severity: 'success',
        summary: this.$t('Mindset360Display.extensionNotification'),
        detail: '',
        life: 3000,
      });
    },
  },
};
</script>
